// Time log at /log?view=day|week&date=YYYY-MM-DD (architecture sprint-04 §6.2; sprint 7 §7.2 layout). A missing view means
// day, so sprint-3 /log?date= links keep working; an invalid view or date is replaced.
import type { LocalDate } from '@shared/calendar';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isValidEntryDate } from '@/components/entries/entry-form-model';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useToday } from '@/lib/use-now';
import { DayView } from './DayView';
import { WeekView } from './WeekView';

type LogView = 'day' | 'week';

const VIEWS: ReadonlyArray<SegmentedOption<LogView>> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
];

function isLogView(v: string | null): v is LogView {
  return v === 'day' || v === 'week';
}

export function TimeLogPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="3xl">
        <Skeleton className="h-48 w-full" />
      </Page>
    );
  return <TimeLog tz={me.timezone} />;
}

function TimeLog({ tz }: { tz: string }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const today = useToday(tz);
  const rawView = params.get('view');
  const rawDate = params.get('date');
  const view: LogView = isLogView(rawView) ? rawView : 'day';
  const date: LocalDate = rawDate !== null && isValidEntryDate(rawDate) ? rawDate : today;
  const invalid = (rawView !== null && !isLogView(rawView)) || (rawDate !== null && !isValidEntryDate(rawDate));

  useEffect(() => {
    if (invalid) setParams({ view, date }, { replace: true });
  }, [invalid, view, date, setParams]);

  const goTo = (next: LocalDate): void => setParams({ view, date: next });

  return (
    <Page width="3xl">
      <PageHeader
        title="Time log"
        hint="Browse and edit your time entries by day or week. Use the arrows to move between periods."
        actions={<SegmentedControl aria-label="View" value={view} onChange={(v) => setParams({ view: v, date })} options={VIEWS} />}
      />
      {view === 'week' ? <WeekView date={date} today={today} onNavigate={goTo} /> : <DayView date={date} today={today} onNavigate={goTo} />}
    </Page>
  );
}
